import { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Loader2, Map as MapIcon, ExternalLink, MapPin } from "lucide-react";
import ConfidenceChip from "./ConfidenceChip";

/**
 * Admin-only Satellite property map. Shows:
 *   • Google Maps Satellite imagery centered on the booking's lat/lng
 *   • Green overlay (circle) approximating the MOWABLE lawn area
 *   • Gray/blue overlay for the non-mowable (house + driveway + non-grass)
 *   • Side panel: Total Property Size, Mowable Lawn Area, Confidence
 *   • Link to open the address in Google Maps for manual measurement
 *
 * This dialog is NEVER shown to customers — iter42 strips all size data from
 * the customer-facing flow.
 */
export default function PropertyMapDialog({ booking, open, onOpenChange }) {
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const lat = booking?.lat;
  const lng = booking?.lng;

  // Lazy-load the Google Maps JS once when the dialog first opens
  useEffect(() => {
    if (!open || mapReady) return;
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) { setMapReady(true); return; }
    if (window.google && window.google.maps) { setMapReady(true); return; }
    const existing = document.getElementById("gmaps-script");
    if (existing) {
      existing.addEventListener("load", () => setMapReady(true));
      return;
    }
    const s = document.createElement("script");
    s.id = "gmaps-script";
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=geometry`;
    s.async = true;
    s.defer = true;
    s.onload = () => setMapReady(true);
    document.head.appendChild(s);
  }, [open, mapReady]);

  // Render the map + overlays once both lat/lng + script are ready
  useEffect(() => {
    if (!open || !mapReady || !mapRef.current || !lat || !lng) return;
    if (!(window.google && window.google.maps)) return;
    const center = { lat: Number(lat), lng: Number(lng) };
    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 19,
      mapTypeId: "satellite",
      tilt: 0,
      disableDefaultUI: false,
      zoomControl: true,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: true,
    });
    new window.google.maps.Marker({ position: center, map, title: booking?.address });
    const mowable = Number(booking?.mowable_sqft || 0);
    const lot = Number(booking?.total_lot_sqft || booking?.square_feet || 0);
    // Approximate radius from sqft (assume roughly square parcel → side ≈ √sqft)
    if (mowable > 0) {
      const radius = Math.sqrt(mowable * 0.0929) * 0.6; // sqft → m², shrink to fit overlay
      new window.google.maps.Circle({
        map, center, radius,
        strokeColor: "#10B981", strokeWeight: 2, strokeOpacity: 0.85,
        fillColor: "#10B981", fillOpacity: 0.35,
      });
    }
    if (lot > mowable && mowable > 0) {
      const outerRadius = Math.sqrt(lot * 0.0929) * 0.65;
      new window.google.maps.Circle({
        map, center, radius: outerRadius,
        strokeColor: "#6366F1", strokeWeight: 1.5, strokeOpacity: 0.7,
        fillColor: "#6366F1", fillOpacity: 0.08, clickable: false,
      });
    }
  }, [open, mapReady, lat, lng, booking]);

  const hasCoords = !!(lat && lng);
  const apiKeyMissing = !process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const gmapsExternal = hasCoords
    ? `https://www.google.com/maps/@${lat},${lng},19z/data=!3m1!1e3`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(booking?.address || "")}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl" data-testid="property-map-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapIcon className="w-5 h-5 text-[#047857]" /> Property map
          </DialogTitle>
          <DialogDescription>
            Verify the mowable lawn area against the satellite imagery. Green = mowable lawn, indigo = excluded (house / driveway / non-grass).
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2 rounded-2xl overflow-hidden border border-gray-200 bg-gray-100 relative" style={{ minHeight: 360 }}>
            {!hasCoords ? (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500 p-6 text-center">
                <div>
                  <MapPin className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                  No GPS coordinates stored on this booking.
                </div>
              </div>
            ) : apiKeyMissing ? (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500 p-6 text-center">
                Google Maps API key not configured. Use the external link below.
              </div>
            ) : !mapReady ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-[#047857]" />
              </div>
            ) : null}
            <div ref={mapRef} className="absolute inset-0" data-testid="property-map-canvas" />
          </div>

          <div className="space-y-3" data-testid="property-map-side-panel">
            <SidePanelRow label="Address" value={booking?.address || "—"} />
            <SidePanelRow label="Total property size" value={booking?.total_lot_sqft ? `${booking.total_lot_sqft.toLocaleString()} sqft` : "—"} />
            <SidePanelRow label="House footprint"    value={booking?.house_sqft       ? `${booking.house_sqft.toLocaleString()} sqft`       : "—"} />
            <SidePanelRow
              label="Mowable lawn area"
              value={booking?.mowable_sqft ? `${booking.mowable_sqft.toLocaleString()} sqft` : "—"}
              accent
            />
            <div className="rounded-2xl bg-gray-50 border border-gray-200 px-3 py-2">
              <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-500">Confidence</div>
              <div className="mt-1">
                {booking?.mowable_confidence
                  ? <ConfidenceChip confidence={booking.mowable_confidence} pct={booking.mowable_confidence_pct} />
                  : <span className="text-[11px] text-gray-500">No measurement yet</span>}
              </div>
            </div>
            {booking?.manual_verification_required && (
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="property-map-manual-banner">
                Manual verification required — confidence below 75%. Adjust boundary before quoting.
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between gap-2 flex-wrap">
          <a
            href={gmapsExternal}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-[0.14em] text-emerald-700 hover:text-emerald-900"
            data-testid="property-map-open-external"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open in Google Maps
          </a>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SidePanelRow({ label, value, accent = false }) {
  return (
    <div className="rounded-2xl bg-gray-50 border border-gray-200 px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-gray-500">{label}</div>
      <div className={`mt-0.5 text-[13px] font-extrabold tabular-nums ${accent ? "text-[#047857]" : "text-gray-900"}`}>{value}</div>
    </div>
  );
}
